import React, { useEffect, useRef, useState } from "react";
import inputStyles from "./sidesheet/addplace/AddSinglePlaceInput.module.css";
import imageStyles from "./AddImageForm.module.css";
import GalleryView, { moveButtonEvents } from "./GalleryView";
import Icon from "../common/Icon";
import { ImageInfo, IMAGEKEY } from "../common/Common";

interface AddImageFormProps {
    savedImages?: ImageInfo[];
    fieldKey?: string;
    limit?: number;
}
/**
 * @author bakim
 * @date 2024-10-08 13:56:57
 * @description 이미지 추가 컴포넌트
 * @param {ImageInfo[]} savedImages 장소에 저장(포함)된 이미지
 * @param {string} fieldKey 지도 데이터의 이미지 필드 키
 * @param {limit} number 최대 추가 가능 이미지 개수
 * @returns
 */
const AddImageForm = ({ savedImages, ...props }: AddImageFormProps) => {
    const allowedFileTypes = ["image/png", "image/jpeg", "image/gif"];
    const fileUploadRef = useRef<HTMLInputElement>(null);

    const [idx, setIdx] = useState<number>(0);
    const [images, setImages] = useState<ImageInfo[]>(savedImages ?? []);
    const [deleteImageId, setDeleteImageId] = useState<string[] | undefined>(undefined);
    const [overLimit, setOverLimit] = useState<boolean>(false);
    const [hover, setHover] = useState<boolean>(false);

    useEffect(() => {
        images.length && setIdx(images.length - 1);

        if (!fileUploadRef.current) return;

        const store = new DataTransfer();
        // 새로 추가한 이미지일 경우 (File type), DB 저장을 위해 form data 에 추가
        images.forEach((image) => image.src instanceof File && store.items.add(image.src));
        fileUploadRef.current.files = store.files;
    }, [images]);

    const onFileAdded = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!e.currentTarget.files) return;
        let files = Array.from(e.currentTarget.files).filter((image: File) => allowedFileTypes.includes(image.type));
        if (props.limit) files = files.slice(0, props.limit - images.length);

        // 새 이미지 추가
        const newImages = files.map((image: File) => {
            return {
                src: image,
            };
        }) as ImageInfo[];

        if (newImages) {
            setImages([...images, ...newImages]);
        }

        setHover(false);
    };

    const onFileUploadClick = () => {
        fileUploadRef.current && fileUploadRef.current.click();
    };

    const deleteImage = () => {
        if (!images.length) return;
        const _item = [...images];
        _item.splice(idx, 1);
        setImages(_item);

        // 제거한 이미지가 저장된 이미지인 경우 삭제 이미지 목록에 추가
        const _images = deleteImageId ?? [];
        images[idx].id && setDeleteImageId([..._images, String(images[idx].id)]);
        overLimit && setOverLimit(false);
    };

    const onLeftClick = (e: React.MouseEvent<HTMLElement>) => {
        overLimit && setOverLimit(false);
        idx > 0 && setIdx(idx - 1);
    };

    const onRightClick = (e: React.MouseEvent<HTMLElement>) => {
        if (idx + 1 === props.limit && images.length === props.limit) {
            setOverLimit(true);
            return;
        }

        idx <= images.length - 1 && setIdx(idx + 1);
    };

    const moveButtonEvents: moveButtonEvents = {
        onLeftClick: onLeftClick,
        onRightClick: onRightClick,
    };

    const handleMouseOver = () => {
        setHover(true);
    };

    const handleMouseOut = () => {
        setHover(false);
    };

    return (
        <div className={`${inputStyles.item} undraggable`}>
            <div className={inputStyles.header}>
                <div className={inputStyles.label}>{"사진 추가"}</div>
                {props.limit && (
                    <div className={`${imageStyles.count} ${overLimit ? imageStyles.caution : ""}`}>
                        {images.length}/{props.limit}
                    </div>
                )}
            </div>
            <div>
                <div className={`${imageStyles.images} `}>
                    {idx !== images.length ? (
                        <div className={imageStyles.frame} onMouseOver={handleMouseOver} onMouseOut={handleMouseOut}>
                            <img
                                src={
                                    images[idx].src instanceof File
                                        ? URL.createObjectURL(images[idx].src as Blob)
                                        : (images[idx].src as string).replaceAll("\\", "/")
                                }
                                className={imageStyles.image}
                            />
                            <div className={`${imageStyles.float} ${inputStyles.icon} ${imageStyles.button}`} onClick={deleteImage}>
                                <Icon name="close" color="black" size="small" />
                            </div>
                        </div>
                    ) : (
                        <>
                            <label
                                className={`${imageStyles.default}`}
                                htmlFor={props.fieldKey ? props.fieldKey : IMAGEKEY}
                                onClick={onFileUploadClick}
                            >
                                <div className={inputStyles.icon}>
                                    <Icon name="plus" color="white" />
                                </div>
                            </label>
                        </>
                    )}
                    <input
                        ref={fileUploadRef}
                        name={props.fieldKey ? props.fieldKey : IMAGEKEY}
                        type="file"
                        accept={allowedFileTypes.join(",")}
                        onChange={onFileAdded}
                        multiple
                    />
                    {savedImages && <input type="hidden" name="deleteImageId" value={deleteImageId} />}
                    <GalleryView moveButtonEvents={moveButtonEvents} />
                </div>
            </div>
        </div>
    );
};

export default AddImageForm;
